import { Copy, ArrowUpRight, Star, ChevronDown } from 'lucide-react';
import { ColorEncyclopediaData } from '../lib/colorEngine';
import { InfoIconTooltip } from './Tooltip';

interface ColorEncyclopediaPanelProps {
  data: ColorEncyclopediaData | null;
  copiedField: string | null;
  onCopy: (value: string, field: string) => void;
  onSetActive?: (hex: string) => void;
  onSave?: (hex: string) => void;
}

const HARMONY_LABELS: { key: keyof NonNullable<ColorEncyclopediaData['harmonies']>; label: string }[] = [
  { key: 'complementary', label: 'Complementary' },
  { key: 'analogous', label: 'Analogous' },
  { key: 'triadic', label: 'Triadic' },
  { key: 'tetradic', label: 'Tetradic' },
  { key: 'splitComplementary', label: 'Split Complementary' },
];

function getReadableTextColor(hex: string): string {
  const value = hex.replace('#', '');
  const expanded = value.length === 3 ? value.split('').map((char) => `${char}${char}`).join('') : value;
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#0f172a' : '#f8fafc';
}

function SwatchGrid({
  title,
  colors,
  prefix,
  copiedField,
  onCopy,
  onSetActive,
  onSave,
}: {
  title: string;
  colors: string[];
  prefix: string;
  copiedField: string | null;
  onCopy: (value: string, field: string) => void;
  onSetActive?: (hex: string) => void;
  onSave?: (hex: string) => void;
}) {
  if (!colors.length) return null;

  return (
    <div className="enc-swatch-grid">
      <h5 className="enc-swatch-grid__title">{title}</h5>
      <div className="enc-swatch-grid__container">
        {colors.map((color) => {
          const fieldKey = `${prefix}-${color}`;
          return (
            <div
              key={fieldKey}
              className="enc-swatch group"
            >
              <button
                type="button"
                className="enc-swatch__face"
                style={{ backgroundColor: color, color: getReadableTextColor(color) }}
                onClick={() => onCopy(color, fieldKey)}
                title="Copy hex to clipboard"
                aria-label={`Copy ${color} to clipboard`}
              >
                <span className="enc-swatch__label">{copiedField === fieldKey ? 'Copied ✓' : color}</span>
              </button>
              <div className="enc-swatch__actions">
                <button
                  type="button"
                  onClick={() => onSetActive?.(color)}
                  className="enc-swatch__action-btn"
                  title="Use color"
                >
                  <ArrowUpRight size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => onSave?.(color)}
                  className="enc-swatch__action-btn"
                  title="Save to favorites"
                >
                  <Star size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HarmonySection({
  harmonies,
  copiedField,
  onCopy,
  onSetActive,
  onSave,
}: {
  harmonies: ColorEncyclopediaData['harmonies'];
  copiedField: string | null;
  onCopy: (value: string, field: string) => void;
  onSetActive?: (hex: string) => void;
  onSave?: (hex: string) => void;
}) {
  if (!harmonies) return null;

  return (
    <div className="enc-harmony-section">
      <h4 className="enc-harmony-section__title">Harmonies</h4>
      <div className="enc-harmony-section__list">
        {HARMONY_LABELS.map(({ key, label }) => {
          const colors = harmonies[key];
          if (!colors?.length) {
            return null;
          }
          return (
            <div key={key} className="enc-harmony-group">
              <span className="enc-harmony-group__label">
                {label}
              </span>
              <div className="enc-harmony-group__chips">
                {colors.map((color, index) => {
                  const fieldKey = `harmony-${key}-${index}-${color}`;
                  return (
                    <div
                      key={fieldKey}
                      className="enc-harmony-chip group"
                    >
                      <span
                        className="enc-harmony-chip__swatch"
                        style={{ backgroundColor: color }}
                        aria-hidden="true"
                      />
                      <span className="enc-harmony-chip__label">
                        {copiedField === fieldKey ? 'Copied ✓' : color}
                      </span>
                      <div className="enc-harmony-chip__actions">
                        <button
                          type="button"
                          onClick={() => onSetActive?.(color)}
                          className="enc-harmony-chip__action-btn"
                          title="Use color"
                        >
                          <ArrowUpRight size={10} />
                        </button>
                        <button
                          type="button"
                          onClick={() => onSave?.(color)}
                          className="enc-harmony-chip__action-btn"
                          title="Save to favorites"
                        >
                          <Star size={10} />
                        </button>
                        <button
                          type="button"
                          onClick={() => onCopy(color, fieldKey)}
                          className="enc-harmony-chip__action-btn"
                          title="Copy hex"
                        >
                          <Copy size={10} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DeltaEList({
  comparisons,
  copiedField,
  onCopy,
  onSetActive,
  onSave,
}: {
  comparisons: ColorEncyclopediaData['deltaEComparisons'];
  copiedField: string | null;
  onCopy: (value: string, field: string) => void;
  onSetActive?: (hex: string) => void;
  onSave?: (hex: string) => void;
}) {
  if (!comparisons.length) return null;

  return (
    <div className="enc-deltae-list">
      <div className="enc-section__heading-row">
        <h4 className="enc-section__heading">ΔE Comparisons</h4>
        <InfoIconTooltip content="ΔE measures perceptual color difference. Values under 1 are imperceptible; 1–5 are noticeable side-by-side; above 5 are clearly different." position="bottom" />
      </div>
      <div className="enc-deltae-list__rows">
        {comparisons.map(({ name, color, deltaE }) => {
          const fieldKey = `deltae-${name}-${color}`;
          return (
            <div
              key={fieldKey}
              className="enc-deltae-row"
            >
              <div className="enc-deltae-row__left">
                <span
                  className="enc-deltae-row__swatch"
                  style={{ backgroundColor: color }}
                  aria-hidden="true"
                />
                <div className="enc-deltae-row__info">
                  <p className="enc-deltae-row__name">{name}</p>
                  <p className="enc-deltae-row__hex">{copiedField === fieldKey ? 'Copied ✓' : color}</p>
                </div>
              </div>
              <div className="enc-deltae-row__right">
                <span className="enc-deltae-row__delta">ΔE {deltaE.toFixed(2)}</span>
                <div className="enc-deltae-row__actions">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onSetActive?.(color); }}
                    className="enc-card__action-btn"
                    title="Use color"
                  >
                    <ArrowUpRight size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onSave?.(color); }}
                    className="enc-card__action-btn"
                    title="Save to favorites"
                  >
                    <Star size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onCopy(color, fieldKey); }}
                    className="enc-card__action-btn"
                    title="Copy hex"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ColorEncyclopediaPanel({ data, copiedField, onCopy, onSetActive, onSave }: ColorEncyclopediaPanelProps) {
  if (!data) return null;

  const { tints, shades, tones, harmonies, deltaEComparisons, gamut } = data;

  return (
    <section className="enc-section">
      <header className="enc-section__header">
        <h3 className="enc-section__title">Color Encyclopedia</h3>
        <p className="enc-section__subtitle">
          Explore extended relationships for the current color, including tone maps, harmonies, gamut coverage, and ΔE
          distances to reference hues.
        </p>
      </header>

      <div className="enc-section__gamut-bar">
        {gamut.map(({ id, label, inGamut }) => (
          <span
            key={id}
            className={`enc-section__gamut-pill ${inGamut ? 'enc-section__gamut-pill--in' : 'enc-section__gamut-pill--out'}`}
          >
            <span className="enc-section__gamut-dot" style={{ backgroundColor: inGamut ? '#16a34a' : '#facc15' }} />
            {label}: {inGamut ? 'In Gamut' : 'Out of Gamut'}
          </span>
        ))}
      </div>

      <DeltaEList comparisons={deltaEComparisons} copiedField={copiedField} onCopy={onCopy} onSetActive={onSetActive} onSave={onSave} />

      <div className="enc-section__columns">
        <details>
          <summary className="enc-details-toggle">
            <ChevronDown size={14} className="enc-details-toggle__icon" />
            Tone Map
          </summary>
          <div className="enc-details-content">
            <SwatchGrid title="Tints" colors={tints} prefix="tint" copiedField={copiedField} onCopy={onCopy} onSetActive={onSetActive} onSave={onSave} />
            <SwatchGrid title="Shades" colors={shades} prefix="shade" copiedField={copiedField} onCopy={onCopy} onSetActive={onSetActive} onSave={onSave} />
            <SwatchGrid title="Tones" colors={tones} prefix="tone" copiedField={copiedField} onCopy={onCopy} onSetActive={onSetActive} onSave={onSave} />
          </div>
        </details>
        <details>
          <summary className="enc-details-toggle">
            <ChevronDown size={14} className="enc-details-toggle__icon" />
            Harmonies
          </summary>
          <HarmonySection harmonies={harmonies} copiedField={copiedField} onCopy={onCopy} onSetActive={onSetActive} onSave={onSave} />
        </details>
      </div>
    </section>
  );
}
